import { useCallback, useLayoutEffect, useRef } from 'preact/hooks';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';

const overflowLonghands = ['overflow-x', 'overflow-y'] as const;

interface OverflowValue {
  value: string;
  priority: string;
}

type OverflowSnapshot = Record<(typeof overflowLonghands)[number], OverflowValue>;

function snapshotBodyOverflow(): OverflowSnapshot {
  const style = document.body.style;
  return {
    'overflow-x': { value: style.getPropertyValue('overflow-x'), priority: style.getPropertyPriority('overflow-x') },
    'overflow-y': { value: style.getPropertyValue('overflow-y'), priority: style.getPropertyPriority('overflow-y') },
  };
}

function restoreBodyOverflow(snapshot: OverflowSnapshot): void {
  const root = getComputedStyle(document.documentElement);
  if (root.overflowX === 'hidden' || root.overflowY === 'hidden') {
    return;
  }
  const style = document.body.style;
  if (!overflowLonghands.some((prop) => style.getPropertyValue(prop) === 'hidden')) {
    return;
  }
  for (const prop of overflowLonghands) {
    const { value, priority } = snapshot[prop];
    if (value === '') {
      style.removeProperty(prop);
      continue;
    }
    style.setProperty(prop, value, priority);
  }
}

// Capture before Stripe mounts: its wallet locks before the merchant click callback.
export function useWalletScrollRestore(): (walletType: string) => void {
  const { inline } = useCheckoutPresentation();
  const snapshotRef = useRef<OverflowSnapshot | null>(null);

  useLayoutEffect(() => {
    if (inline) {
      snapshotRef.current = snapshotBodyOverflow();
    }
  }, []);

  return useCallback((walletType: string) => {
    const snapshot = snapshotRef.current;
    if (!snapshot || walletType !== 'google_pay') {
      return;
    }
    // Completed Google Pay + 3DS can leave a stale lock. A publisher body-only lock can conflict.
    restoreBodyOverflow(snapshot);
  }, []);
}
